import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { UserBlockReason } from '@masters-club/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BLOCK_REASON_LABELS, type BlockUserPayload } from '../types';

const REASON_MIN = 3;
const REASON_MAX = 500;
const DEFAULT_REASON_CODE: UserBlockReason = UserBlockReason.TOS_VIOLATION;

function getReasonHint(trimmedLength: number): string {
  if (trimmedLength < REASON_MIN) {
    return `Минимум ${REASON_MIN} символа`;
  }
  if (trimmedLength > REASON_MAX) {
    return `Максимум ${REASON_MAX} символов`;
  }
  return '\u00A0';
}

const REASON_CODES: UserBlockReason[] = [
  UserBlockReason.SPAM,
  UserBlockReason.ABUSE,
  UserBlockReason.FRAUD,
  UserBlockReason.PAYMENT_CHARGEBACK,
  UserBlockReason.TOS_VIOLATION,
  UserBlockReason.OTHER,
];

interface BlockUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (payload: BlockUserPayload) => Promise<void>;
  /** Отображаемое имя юзера для заголовка. Для множественной блокировки — "3 пользователей". */
  userName: string;
}

/**
 * Диалог блокировки пользователя.
 * Поле reason — обязательное, 3..500 символов (trim).
 * Поле reasonCode — обязательное, одно из UserBlockReason.
 */
export function BlockUserDialog({
  open,
  onOpenChange,
  onConfirm,
  userName,
}: BlockUserDialogProps): React.ReactElement {
  const [reasonCode, setReasonCode] = useState<UserBlockReason>(DEFAULT_REASON_CODE);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReasonCode(DEFAULT_REASON_CODE);
      setReason('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const trimmedLength = reason.trim().length;
  const isReasonValid = trimmedLength >= REASON_MIN && trimmedLength <= REASON_MAX;

  const handleSubmit = async (): Promise<void> => {
    if (!isReasonValid || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm({ reason: reason.trim(), reasonCode });
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Не удалось заблокировать пользователя';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-destructive" />
            Заблокировать {userName}
          </DialogTitle>
          <DialogDescription>
            Пользователь получит 403 ACCOUNT_BLOCKED на всех запросах и будет выкинут из
            активных сессий. Причина будет видна только администраторам.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="block-reason-code">Код причины</Label>
            <Select
              value={reasonCode}
              onValueChange={(value) => setReasonCode(value as UserBlockReason)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="block-reason-code">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASON_CODES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {BLOCK_REASON_LABELS[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-reason">
              Подробная причина <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="block-reason"
              placeholder="Опишите, почему блокируется пользователь (3–500 символов)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={REASON_MAX}
              disabled={isSubmitting}
              className={
                reason.length > 0 && !isReasonValid ? 'border-destructive' : undefined
              }
            />
            <div className="flex items-center justify-between text-xs">
              <span
                className={
                  reason.length > 0 && !isReasonValid
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }
              >
                {getReasonHint(trimmedLength)}
              </span>
              <span className="text-muted-foreground">
                {trimmedLength} / {REASON_MAX}
              </span>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!isReasonValid || isSubmitting}
          >
            {isSubmitting ? 'Блокировка...' : 'Заблокировать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
